import type {
  LightingAdvisorQuestion,
  LightingAdvisorRecommendationFlag,
  LightingAdvisorRecommendationsResponse,
  LightingAdvisorRecommendedProduct,
  LightingAdvisorResults
} from '~/types/lightingAdvisor';
import { normalizeQuestionInputs } from '~/utils/lightingAdvisor/mapQuestionInputsToSelectOptions';

function dedupeStringList(values: string[] | undefined): string[] | undefined {
  if (!values?.length) {
    return values;
  }
  return [...new Set(values)];
}

const KNOWN_FLAGS: readonly LightingAdvisorRecommendationFlag[] = [
  'best_technical_fit',
  'price_performance_ratio'
];

/** Some backends use synonyms / Lens-Finder naming. Map them to canonical flags. */
const FLAG_ALIASES: Record<string, LightingAdvisorRecommendationFlag> = {
  best_technical_fit: 'best_technical_fit',
  best_fit: 'best_technical_fit',
  bestfit: 'best_technical_fit',
  price_performance_ratio: 'price_performance_ratio',
  price_performance: 'price_performance_ratio',
  best_price_performance: 'price_performance_ratio'
};

function toCanonicalFlag(
  raw: unknown
): LightingAdvisorRecommendationFlag | null {
  if (typeof raw !== 'string') {
    return null;
  }
  return FLAG_ALIASES[raw.trim().toLowerCase()] ?? null;
}

/**
 * Normalizes recommendation flags from any of the shapes a backend may use:
 * - per-product `flags: string[]`
 * - per-product boolean fields (`best_technical_fit: true`)
 * - a top-level `flags` map of `{ <flag>: string[] (skus) }`
 * into a canonical per-product `flags` array.
 */
function normalizeRecommendedProducts(
  rawProducts: unknown,
  topLevelFlags: unknown
): LightingAdvisorRecommendedProduct[] | undefined {
  if (!Array.isArray(rawProducts)) {
    return undefined;
  }

  const flagSkuSets = new Map<LightingAdvisorRecommendationFlag, Set<string>>();
  if (topLevelFlags != null && typeof topLevelFlags === 'object') {
    for (const [key, value] of Object.entries(
      topLevelFlags as Record<string, unknown>
    )) {
      const flag = toCanonicalFlag(key);
      if (!flag || !Array.isArray(value)) {
        continue;
      }
      const skuSet = flagSkuSets.get(flag) ?? new Set<string>();
      for (const sku of value) {
        const normalized = String(sku ?? '').trim();
        if (normalized) {
          skuSet.add(normalized);
        }
      }
      flagSkuSets.set(flag, skuSet);
    }
  }

  return rawProducts
    .map((entry): LightingAdvisorRecommendedProduct | null => {
      if (entry == null || typeof entry !== 'object') {
        return null;
      }
      const record = entry as Record<string, unknown>;
      const sku = String(record.sku ?? '').trim();
      if (!sku) {
        return null;
      }

      const flags = new Set<LightingAdvisorRecommendationFlag>();

      // (a) per-product flags array
      if (Array.isArray(record.flags)) {
        for (const candidate of record.flags) {
          const flag = toCanonicalFlag(candidate);
          if (flag) {
            flags.add(flag);
          }
        }
      }

      // (b) per-product boolean fields
      for (const flag of KNOWN_FLAGS) {
        for (const alias of Object.keys(FLAG_ALIASES)) {
          if (FLAG_ALIASES[alias] === flag && record[alias] === true) {
            flags.add(flag);
          }
        }
      }

      // (c) top-level flag -> sku map
      for (const [flag, skuSet] of flagSkuSets) {
        if (skuSet.has(sku)) {
          flags.add(flag);
        }
      }

      const rank =
        typeof record.rank === 'number'
          ? record.rank
          : record.rank == null
            ? null
            : Number(record.rank);

      return {
        sku,
        ordernumber:
          record.ordernumber == null ? null : String(record.ordernumber),
        rank: Number.isNaN(rank as number) ? null : rank,
        ...(flags.size > 0 ? { flags: [...flags] } : {})
      };
    })
    .filter((item): item is LightingAdvisorRecommendedProduct => item != null);
}

/** Dev API may return `results: []` before any answers are submitted. */
export function normalizeLightingAdvisorResults(
  raw: unknown
): LightingAdvisorResults {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }

  const results = raw as LightingAdvisorResults;
  return {
    ...results,
    light_category: dedupeStringList(results.light_category),
    light_type: dedupeStringList(results.light_type),
    light_color: dedupeStringList(results.light_color)
  };
}

export function normalizeLightingAdvisorResponse(
  raw: unknown
): LightingAdvisorRecommendationsResponse | null {
  if (raw == null || typeof raw !== 'object') {
    return null;
  }
  const payload = raw as Record<string, unknown>;
  const questions = payload.questions;
  if (!Array.isArray(questions)) {
    return null;
  }
  return {
    results: normalizeLightingAdvisorResults(payload.results),
    questions: (questions as LightingAdvisorQuestion[]).map((question) => ({
      ...question,
      inputs: normalizeQuestionInputs(
        (question as LightingAdvisorQuestion & { inputs?: unknown }).inputs
      )
    })),
    recommended_products: normalizeRecommendedProducts(
      payload.recommended_products,
      payload.flags
    )
  };
}

export function hasLightingResultFilters(
  results: LightingAdvisorResults | null | undefined
): boolean {
  if (!results) {
    return false;
  }
  return (
    (results.light_category?.length ?? 0) > 0 ||
    (results.light_type?.length ?? 0) > 0 ||
    (results.light_color?.length ?? 0) > 0 ||
    Boolean(results.peak_wavelength_typical) ||
    results.working_distance != null ||
    (results.max_object_size?.length != null &&
      results.max_object_size?.width != null)
  );
}
